import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, CheckCircle, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrgInvitePopup({ user, onClose }) {
  const [pendingInvites, setPendingInvites] = useState([]);
  const [currentInvite, setCurrentInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (user) {
      checkPendingInvites();
    }
  }, [user]);

  const checkPendingInvites = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/me/pending-org-invites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingInvites(data.pending_invites || []);
        if (data.pending_invites?.length > 0) {
          setCurrentInvite(data.pending_invites[0]);
        }
      }
    } catch (err) {
      console.error("Error checking pending invites:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!currentInvite) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/me/accept-org-invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(currentInvite)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Invite accepted!");
        moveToNextInvite();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to accept invite");
      }
    } catch (err) {
      toast.error("Error accepting invite");
    } finally {
      setProcessing(false);
    }
  };

  const handleDismiss = async () => {
    if (!currentInvite) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/me/dismiss-org-invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(currentInvite)
      });
      moveToNextInvite();
    } catch (err) {
      console.error("Error dismissing invite:", err);
    } finally {
      setProcessing(false);
    }
  };

  const moveToNextInvite = () => {
    const remaining = pendingInvites.filter((inv) => inv !== currentInvite);
    setPendingInvites(remaining);
    if (remaining.length > 0) {
      setCurrentInvite(remaining[0]);
    } else {
      setCurrentInvite(null);
      onClose?.();
    }
  };

  if (loading || !currentInvite) return null;

  const getInviteTitle = () => {
    switch (currentInvite.type) {
      case "event_partner":
        return "You've been added as a Partner!";
      case "event_sponsor":
        return "You've been added as a Sponsor!";
      case "event_team":
        return "You've been added to the Team!";
      case "org_invite":
        return "Host invitation";
      default:
        return "You have an invitation!";
    }
  };

  const getRoleBadge = () => {
    const role = currentInvite.role || currentInvite.label;
    if (!role) return null;
    return (
      <Badge variant="secondary" className="capitalize">
        {role}
      </Badge>
    );
  };

  return (
    <Dialog open={!!currentInvite} onOpenChange={() => handleDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            {getInviteTitle()}
          </DialogTitle>
          <DialogDescription className="pt-2">
            <span className="font-medium text-gray-900">{currentInvite.invited_by}</span> added you as a contact
            {currentInvite.org_name && (
              <>
                {" "}for <span className="font-medium text-gray-900">{currentInvite.org_name}</span>
              </>
            )}
            {currentInvite.event_name && (
              <>
                {" "}for the event <span className="font-medium text-purple-600">{currentInvite.event_name}</span>
              </>
            )}
            .
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            {currentInvite.org_name && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Host</span>
                <span className="font-medium">{currentInvite.org_name}</span>
              </div>
            )}
            {currentInvite.event_name && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Event</span>
                <span className="font-medium">{currentInvite.event_name}</span>
              </div>
            )}
            {currentInvite.role && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Role</span>
                {getRoleBadge()}
              </div>
            )}
            {currentInvite.tier && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Sponsor Tier</span>
                <Badge className="capitalize">{currentInvite.tier}</Badge>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-600 mt-4">
            Would you like to add this organization to your profile?
          </p>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleDismiss}
            disabled={processing}
            className="flex-1 sm:flex-none"
          >
            <X className="w-4 h-4 mr-1" />
            Not Now
          </Button>
          <Button
            onClick={handleAccept}
            disabled={processing}
            className="flex-1 sm:flex-none"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            {processing ? "Processing..." : "Add to Profile"}
          </Button>
        </DialogFooter>

        {pendingInvites.length > 1 && (
          <p className="text-xs text-gray-400 text-center mt-2">
            {pendingInvites.length - 1} more invite{pendingInvites.length > 2 ? "s" : ""} pending
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
